import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import { ValidationError } from '../../components';
import IconCollection from '../IconCollection/IconCollection';

import css from './CustomDropdown.module.css';

const CustomDropdownComponent = props => {
  const {
    rootClassName,
    className,
    id,
    label,
    input,
    meta,
    options,
    placeholder,
    onChange,
    disabled,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const dropdownRef = useRef(null);

  if (label && !id) {
    throw new Error('id required when a label is given');
  }

  const { valid, invalid, touched, error } = meta;
  const hasError = touched && invalid && error;

  // Find selected option based on input value
  useEffect(() => {
    if (input.value && options) {
      const option = options.find(opt => opt.value === input.value);
      setSelectedOption(option || null);
    } else {
      setSelectedOption(null);
    }
  }, [input.value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = option => {
    setSelectedOption(option);
    input.onChange(option.value);
    setIsOpen(false);
    
    if (onChange) {
      onChange(option.value);
    }
  };

  const classes = classNames(rootClassName || css.root, className, {
    [css.disabled]: disabled,
  });

  const dropdownClasses = classNames(css.dropdown, {
    [css.dropdownError]: hasError,
    [css.dropdownOpen]: isOpen,
  });

  return (
    <div className={classes} ref={dropdownRef}>
      {label ? (
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
      ) : null}
      
      <div className={css.dropdownContainer}>
        <button
          type="button"
          className={dropdownClasses}
          onClick={handleToggle}
          disabled={disabled}
          id={id}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className={classNames(css.selectedText, {
            [css.selectedTextPlaceholder]: !selectedOption
          })}>
            {selectedOption ? selectedOption.label : placeholder || 'Select an option'}
          </span>
          <span className={css.arrow}>
            <IconCollection name={isOpen ? 'arrowUp' : 'arrowDown'} />
          </span>
        </button>

        {isOpen && (
          <ul className={css.optionsList} role="listbox">
            {options && options.length > 0 ? (
              options.map(option => {
                const isSelected = selectedOption?.value === option.value;
                const optionClasses = classNames(css.option, {
                  [css.optionSelected]: isSelected,
                });

                return (
                  <li
                    key={option.value}
                    className={optionClasses}
                    onClick={() => handleSelect(option)}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {option.label}
                  </li>
                );
              })
            ) : (
              <li className={css.optionEmpty}>No options available</li>
            )}
          </ul>
        )}
      </div>

      <ValidationError fieldMeta={meta} />
    </div>
  );
};

CustomDropdownComponent.defaultProps = {
  rootClassName: null,
  className: null,
  id: null,
  label: null,
  options: [],
  placeholder: 'Select an option',
  onChange: null,
  disabled: false,
};

const { string, object, arrayOf, shape, func, bool } = PropTypes;

CustomDropdownComponent.propTypes = {
  rootClassName: string,
  className: string,
  id: string,
  label: string,
  input: object.isRequired,
  meta: object.isRequired,
  options: arrayOf(
    shape({
      value: string.isRequired,
      label: string.isRequired,
    })
  ),
  placeholder: string,
  onChange: func,
  disabled: bool,
};

const CustomDropdown = props => {
  return <Field component={CustomDropdownComponent} {...props} />;
};

export default CustomDropdown;
